import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { LoadingButton } from '@mui/lab';

import { QueueServicePattern } from '../../../api/queuePatterns';

export interface QueueOption {
  id: string;
  name: string;
  code?: string;
}

export interface QueuePatternFormValues {
  name: string;
  description: string;
  sequence: string[];
  isActive: boolean;
  autoReset: boolean;
}

interface QueuePatternFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: QueuePatternFormValues) => Promise<void> | void;
  queues: QueueOption[];
  loading?: boolean;
  pattern?: QueueServicePattern | null;
}

const defaultFormValues: QueuePatternFormValues = {
  name: '',
  description: '',
  sequence: [],
  isActive: true,
  autoReset: true,
};

const QueuePatternFormDrawer: React.FC<QueuePatternFormDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  queues,
  loading = false,
  pattern = null,
}) => {
  const [formValues, setFormValues] = useState<QueuePatternFormValues>(defaultFormValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const queueOptions = useMemo(() => queues || [], [queues]);
  const queueMap = useMemo(() => {
    const map = new Map<string, QueueOption>();
    queueOptions.forEach((queue) => {
      map.set(queue.id, queue);
    });
    return map;
  }, [queueOptions]);

  useEffect(() => {
    if (!open) {
      setFormValues(defaultFormValues);
      setErrors({});
      return;
    }

    if (pattern) {
      setFormValues({
        name: pattern.name,
        description: pattern.description ?? '',
        sequence: pattern.sequence.slice(),
        isActive: pattern.isActive,
        autoReset: pattern.autoReset,
      });
      setErrors({});
      return;
    }

    setFormValues((prev) => ({
      ...defaultFormValues,
      sequence: prev.sequence.length > 0 ? prev.sequence : [],
    }));
    setErrors({});
  }, [open, pattern]);

  const updateSequence = (updater: (prev: string[]) => string[]) => {
    setFormValues((prev) => ({
      ...prev,
      sequence: updater(prev.sequence),
    }));
  };

  const handleAddStep = () => {
    if (queueOptions.length === 0) {
      return;
    }
    updateSequence((prev) => [...prev, queueOptions[0].id]);
  };

  const handleRemoveStep = (index: number) => {
    updateSequence((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleMoveStep = (index: number, direction: 'up' | 'down') => {
    updateSequence((prev) => {
      const next = prev.slice();
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= next.length) {
        return prev;
      }
      const tmp = next[targetIndex];
      next[targetIndex] = next[index];
      next[index] = tmp;
      return next;
    });
  };

  const handleQueueChange = (index: number, queueId: string) => {
    updateSequence((prev) => {
      const next = prev.slice();
      next[index] = queueId;
      return next;
    });
  };

  const handleChange = (field: keyof QueuePatternFormValues, value: string | boolean | string[]) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validate = (): boolean => {
    const validationErrors: Record<string, string> = {};

    if (!formValues.name.trim()) {
      validationErrors.name = 'Укажите название шаблона';
    }

    if (!formValues.sequence || formValues.sequence.length === 0) {
      validationErrors.sequence = 'Добавьте минимум одну очередь в последовательность';
    } else if (formValues.sequence.some((queueId) => !queueId)) {
      validationErrors.sequence = 'Выберите очередь для каждого шага последовательности';
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    await onSubmit({
      ...formValues,
      name: formValues.name.trim(),
      description: formValues.description.trim(),
      sequence: formValues.sequence.filter(Boolean),
    });
  };

  const renderSequenceEditor = () => {
    if (queueOptions.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Нет доступных очередей. Добавьте очереди перед созданием шаблона.
        </Typography>
      );
    }

    return (
      <Stack spacing={1.5}>
        {formValues.sequence.map((queueId, index) => {
          const queue = queueMap.get(queueId);
          const label = queue ? `${queue.name}${queue.code ? ` (${queue.code})` : ''}` : queueId;

          return (
            <Box
              key={`${queueId}-${index}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ width: 32 }}>
                {index + 1}.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id={`queue-step-${index}`}>Очередь</InputLabel>
                <Select
                  labelId={`queue-step-${index}`}
                  label="Очередь"
                  value={queueId || ''}
                  onChange={(event) => handleQueueChange(index, event.target.value as string)}
                >
                  {queueOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                      {option.code ? ` (${option.code})` : ''}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{label}</FormHelperText>
              </FormControl>
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  aria-label="Поднять выше"
                  size="small"
                  disabled={index === 0}
                  onClick={() => handleMoveStep(index, 'up')}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Опустить ниже"
                  size="small"
                  disabled={index === formValues.sequence.length - 1}
                  onClick={() => handleMoveStep(index, 'down')}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Удалить шаг"
                  size="small"
                  onClick={() => handleRemoveStep(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          );
        })}

        <Button
          startIcon={<AddCircleOutlineIcon />}
          variant="outlined"
          size="small"
          onClick={handleAddStep}
        >
          Добавить шаг
        </Button>
      </Stack>
    );
  };

  const title = pattern ? 'Редактирование шаблона' : 'Новый шаблон обслуживания';

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 420, maxWidth: '100%' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Укажите последовательность очередей и параметры шаблона.
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflowY: 'auto' }}>
        <TextField
          label="Название"
          value={formValues.name}
          error={Boolean(errors.name)}
          helperText={errors.name}
          onChange={(event) => handleChange('name', event.target.value)}
          fullWidth
        />

        <TextField
          label="Описание"
          value={formValues.description}
          onChange={(event) => handleChange('description', event.target.value)}
          fullWidth
          multiline
          minRows={2}
        />

        <Stack direction="row" spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={formValues.isActive}
                onChange={(event) => handleChange('isActive', event.target.checked)}
              />
            }
            label="Активен"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formValues.autoReset}
                onChange={(event) => handleChange('autoReset', event.target.checked)}
              />
            }
            label="Авто-сброс последовательности"
          />
        </Stack>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Последовательность очередей
          </Typography>
          {errors.sequence && (
            <FormHelperText error sx={{ mb: 1 }}>
              {errors.sequence}
            </FormHelperText>
          )}
          {renderSequenceEditor()}
        </Box>
      </Box>
      <Divider />
      <Box sx={{ p: 3, display: 'flex', gap: 1.5 }}>
        <Button onClick={onClose} fullWidth variant="outlined">
          Отмена
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          fullWidth
          variant="contained"
          loading={loading}
          disabled={queueOptions.length === 0}
        >
          Сохранить
        </LoadingButton>
      </Box>
    </Drawer>
  );
};

export default QueuePatternFormDrawer;
